import { ChangeDetectionStrategy, Component, OnDestroy, computed, inject, input, signal } from '@angular/core';

import { Button, ButtonIcon, ButtonLabel } from 'primeng/button';

import { TranslationModule } from '@core/translation/translation.module';
import { U2bTabSwitcherComponent, U2bTabSwitcherItem } from '@u2bUi/tab-switcher/u2b-tab-switcher.component';

import { BookingV2Facade } from '../../services/booking-v2.facade';
import { DocumentsTabComponent } from './tabs/documents-tab.component';
import { ElectricMetersTabComponent } from './tabs/electric-meters-tab.component';
import { KeysTabComponent } from './tabs/keys-tab.component';
import { ServicesTabComponent } from './tabs/services-tab.component';
import { TravelersTabComponent } from './tabs/travelers-tab.component';
import { BOOKING_V2_ADVANCED_SERVICES_TAB_LABEL_KEY } from './booking-v2-advanced-i18n';

type AdvancedTab = 'travelers' | 'power' | 'keys' | 'services' | 'documents';

@Component({
    selector: 'booking-v2-advanced-step',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        Button,
        ButtonIcon,
        ButtonLabel,
        TranslationModule,
        U2bTabSwitcherComponent,
        TravelersTabComponent,
        ElectricMetersTabComponent,
        KeysTabComponent,
        ServicesTabComponent,
        DocumentsTabComponent,
    ],
    templateUrl: './advanced-step.component.html',
    host: { class: 'block' },
})
export class AdvancedStepComponent implements OnDestroy {

    private readonly facade = inject(BookingV2Facade);

    // Read-only mode (summary step): hides the step header and forwards to each
    // tab so their add/edit/remove affordances disappear.
    readonly readOnly = input<boolean>(false);

    readonly activeTab = signal<AdvancedTab>('travelers');

    readonly tabs = computed<U2bTabSwitcherItem<AdvancedTab>[]>(() => [
        { id: 'travelers', icon: 'group', labelKey: 'bookingV2.advanced.tabs.travelers', count: this.facade.travelers().length },
        { id: 'power', icon: 'bolt', labelKey: 'bookingV2.advanced.tabs.power', count: this.facade.electricMeterAssignments().length },
        { id: 'keys', icon: 'vpn_key', labelKey: 'bookingV2.advanced.tabs.keys', count: this.facade.keys().length },
        { id: 'services', icon: 'shopping_bag', labelKey: BOOKING_V2_ADVANCED_SERVICES_TAB_LABEL_KEY, count: this.facade.services().length },
        { id: 'documents', icon: 'description', labelKey: 'bookingV2.advanced.tabs.documents', count: this.facade.documents().length },
    ]);

    private readonly tabIndex = computed(() =>
        this.tabs().findIndex(t => t.id === this.activeTab()),
    );

    // Neighbouring sections drive the in-card pager. `null` at the ends hides the
    // back arrow (first tab) and turns the forward control into "continue to
    // summary" (last tab).
    readonly previousTab = computed<U2bTabSwitcherItem<AdvancedTab> | null>(() => {
        const i = this.tabIndex();
        return i > 0 ? this.tabs()[i - 1] : null;
    });

    readonly nextTab = computed<U2bTabSwitcherItem<AdvancedTab> | null>(() => {
        const tabs = this.tabs();
        const i = this.tabIndex();
        return i >= 0 && i < tabs.length - 1 ? tabs[i + 1] : null;
    });

    selectTab(tab: AdvancedTab): void {
        if (tab === this.activeTab()) {
            return;
        }
        // Switching destroys the current tab (and its draft) — confirm first.
        this.facade.guardAdvancedDraft(() => this.activeTab.set(tab));
    }

    goToPreviousTab(): void {
        const prev = this.previousTab();
        if (prev) {
            this.selectTab(prev.id);
        }
    }

    // Pages to the next advanced section; from the last section it continues the
    // wizard to the summary. Without this the footer "Weiter" jumps straight from
    // the first section to the summary, skipping the other tabs (6585).
    goToNextTab(): void {
        const next = this.nextTab();
        if (next) {
            this.selectTab(next.id);
            return;
        }
        this.facade.advanceToNextStep();
    }

    ngOnDestroy(): void {
        // Leaving the advanced area entirely (step change, advanced mode off):
        // any in-progress draft is gone, so the flag must not linger.
        this.facade.advancedDraftDirty.set(false);
    }
}
